import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import enUI from '@linktivity/link-ui/lang/en';
import jaUI from '@linktivity/link-ui/lang/ja';
import koUI from '@linktivity/link-ui/lang/ko';
import zhUI from '@linktivity/link-ui/lang/zh';
import ztUI from '@linktivity/link-ui/lang/zt';
import { JAPANESE_LOCALE, LOCALE_OPTIONS } from '@link/share/constants';
import enCountry from './country/en';
import jaCountry from './country/ja';
import koCountry from './country/ko';
import zhCountry from './country/zh';
import ztCountry from './country/zt';
import enFAQ from './faq/en';
import jaFAQ from './faq/ja';
import koFAQ from './faq/ko';
import zhFAQ from './faq/zh';
import ztFAQ from './faq/zt';
import enJapanPrefecture from './japanPrefectures/en';
import jaJapanPrefecture from './japanPrefectures/ja';
import koJapanPrefecture from './japanPrefectures/ko';
import zhJapanPrefecture from './japanPrefectures/zh';
import ztJapanPrefecture from './japanPrefectures/zt';
import shareEnText from './lang/en';
import shareJaText from './lang/ja';
import shareKoText from './lang/ko';
import shareZhText from './lang/zh';
import shareZtText from './lang/zt';
import enLaw from './law/en';
import jaLaw from './law/ja';
import koLaw from './law/ko';
import zhLaw from './law/zh';
import ztLaw from './law/zt';
import enPrivacy from './privacy/en';
import jaPrivacy from './privacy/ja';
import koPrivacy from './privacy/ko';
import zhPrivacy from './privacy/zh';
import ztPrivacy from './privacy/zt';
import enTerms from './terms/en';
import jaTerms from './terms/ja';
import koTerms from './terms/ko';
import zhTerms from './terms/zh';
import ztTerms from './terms/zt';

export const DEFAULT_LOCALE = JAPANESE_LOCALE;
export const LOCALE_LIST: string[] = LOCALE_OPTIONS.map(locale => locale.value);

export const getLocaleValues = (
  options: readonly (typeof LOCALE_OPTIONS)[number][]
): string[] => options.map(locale => locale.value);

interface ResourcesRecord {
  [key: string]: string | string[] | ResourcesRecord | Record<string, unknown>;
}

interface Resources {
  jaText?: ResourcesRecord;
  enText?: ResourcesRecord;
  koText?: ResourcesRecord;
  zhText?: ResourcesRecord;
  ztText?: ResourcesRecord;
}

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

export const resources = ({
  jaText,
  enText,
  koText,
  zhText,
  ztText
}: Resources) => {
  return {
    ja: {
      translation: {
        share: {
          ...shareJaText
        },
        ...jaText,
        ...jaUI,
        country: {
          ...jaCountry
        },
        prefecture: {
          ...jaJapanPrefecture
        },

        ...jaFAQ,
        ...jaPrivacy,
        ...jaTerms,
        ...jaLaw
      }
    },
    en: {
      translation: {
        share: {
          ...shareEnText
        },
        ...enText,
        ...enUI,
        country: {
          ...enCountry
        },
        prefecture: {
          ...enJapanPrefecture
        },
        ...enFAQ,
        ...enPrivacy,
        ...enTerms,
        ...enLaw
      }
    },
    ko: {
      translation: {
        share: {
          ...shareKoText
        },
        ...koText,
        ...koUI,
        country: {
          ...koCountry
        },
        prefecture: {
          ...koJapanPrefecture
        },
        ...koFAQ,
        ...koPrivacy,
        ...koTerms,
        ...koLaw
      }
    },
    zh: {
      translation: {
        share: {
          ...shareZhText
        },
        ...zhText,
        ...zhUI,
        country: {
          ...zhCountry
        },
        prefecture: {
          ...zhJapanPrefecture
        },
        ...zhFAQ,
        ...zhPrivacy,
        ...zhTerms,
        ...zhLaw
      }
    },
    zt: {
      translation: {
        share: {
          ...shareZtText
        },
        ...ztText,
        ...ztUI,
        country: {
          ...ztCountry
        },
        prefecture: {
          ...ztJapanPrefecture
        },
        ...ztFAQ,
        ...ztPrivacy,
        ...ztTerms,
        ...ztLaw
      }
    }
  };
};

export function setLocale(locale: string): string {
  void i18n.changeLanguage(locale);
  document.documentElement.setAttribute('lang', locale);
  localStorage.setItem('locale', locale);
  return locale;
}

function getLocaleParams(supportedLocales: string[]) {
  const searchParams = new URLSearchParams(location.search);
  const lang = searchParams.get('lang') || '';
  if (supportedLocales.includes(lang)) {
    return lang;
  }
}

interface InitI18nOptions extends Resources {
  defaultLocale?: string;
  supportedLocales?: string[];
}

export const initI18n = ({
  jaText,
  enText,
  koText,
  zhText,
  ztText,
  defaultLocale = DEFAULT_LOCALE,
  supportedLocales = LOCALE_LIST
}: InitI18nOptions) => {
  void i18n.use(initReactI18next).init({
    lng: defaultLocale,
    returnNull: false,
    resources: resources({
      jaText,
      enText,
      koText,
      zhText,
      ztText
    })
  });

  const resolvedLocale =
    getLocaleParams(supportedLocales) ||
    localStorage.getItem('locale') ||
    defaultLocale;
  setLocale(
    supportedLocales.includes(resolvedLocale) ? resolvedLocale : defaultLocale
  );
};

export default i18n;
